import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { seminarApi, bookingApi } from '../../lib/api';
import { PrerequisitesDialog } from '../common/PrerequisitesDialog';
import LegalDialog from '../common/LegalDialog';
import { AdminPreviewBanner } from '../common/AdminPreviewBanner';
import { usePublicHeaderConfig } from '../../hooks/usePublicHeaderConfig';
import { formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';
import {
    Calendar, MapPin, Clock, Users, Heart, ArrowLeft,
    ArrowRight, Award, Building2, GraduationCap, ExternalLink,
    FileText, User, Tag, Monitor, Wifi, CheckCircle, Info
} from 'lucide-react';

const EVENT_CATEGORY_LABELS = {
    fortbildung: 'Fortbildung',
    weiterbildung: 'Weiterbildung',
    ausbildung: 'Ausbildung',
};

const EVENT_FORMAT_CONFIG = {
    praesenz: { label: 'Präsenz', icon: MapPin, color: 'bg-green-50 text-green-700' },
    hybrid: { label: 'Hybrid', icon: Wifi, color: 'bg-purple-50 text-purple-700' },
    online: { label: 'Online', icon: Monitor, color: 'bg-sky-50 text-sky-700' },
};

function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
        let d;
        if (dateStr.includes('.')) {
            const parts = dateStr.split('.');
            if (parts.length === 3) {
                d = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
            }
        } else {
            d = new Date(dateStr + 'T00:00:00');
        }
        if (!d || isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
    } catch {
        return dateStr;
    }
}

export default function SeminarDetailPage() {
    const { id } = useParams();
    const [seminar, setSeminar] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [booking, setBooking] = useState(false);
    const [prereqOpen, setPrereqOpen] = useState(false);
    const [headerLogo, setHeaderLogo] = useState(null);
    const [legalOpen, setLegalOpen] = useState(null);
    const headerCfg = usePublicHeaderConfig();
    const [selectedDiscountIdx, setSelectedDiscountIdx] = useState(-1); // -1 = no discount
    const [vereinName, setVereinName] = useState('');
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const BACKEND_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL || "");

    useEffect(() => {
        loadSeminar();
        const img = new Image();
        img.onload = () => setHeaderLogo(`${BACKEND_URL}/api/settings/logos/header`);
        img.onerror = () => setHeaderLogo(null);
        img.src = `${BACKEND_URL}/api/settings/logos/header`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadSeminar = async () => {
        try {
            const response = await seminarApi.getPublicOne(id);
            setSeminar(response.data);
        } catch (error) {
            if (error.response?.status === 404) {
                setNotFound(true);
            }
        } finally {
            setLoading(false);
        }
    };

    const isExternalOrganizer = seminar && (seminar.organizer_type || 'bgpr') === 'external';
    // Visual treatment is reserved for true external offers; BGPR seminars
    // that merely use an external registration link still appear as BGPR.
    const isExternal = !!isExternalOrganizer;
    const hasExternalRegistration = seminar && (isExternalOrganizer || !!seminar.external_link);
    const isFull = seminar && seminar.current_participants >= seminar.max_participants;

    // Anmeldeschluss vom Backend (effective + closed flag)
    const registrationClosed = !!seminar?.registration_closed;
    const deadlineRaw = seminar?.registration_deadline_effective;
    const deadlineDisplay = deadlineRaw ? formatDate(String(deadlineRaw).slice(0,10)) : '';

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin h-10 w-10 border-3 border-primary border-t-transparent rounded-full" />
            </div>
        );
    }

    if (notFound || !seminar) {
        return (
            <div className="min-h-screen bg-gray-50">
                <header className="bg-secondary">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
                        <Link to="/seminare" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
                            <ArrowLeft className="h-4 w-4" />
                            Zurück zur Übersicht
                        </Link>
                    </div>
                </header>
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-24 text-center">
                    <h2 className="text-2xl font-bold text-gray-700 mb-2">Seminar nicht gefunden</h2>
                    <p className="text-gray-500 mb-6">Dieses Seminar existiert nicht oder ist nicht mehr verfügbar.</p>
                    <Link to="/seminare" className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/80 transition-colors font-medium">
                        <ArrowLeft className="h-4 w-4" />
                        Alle Seminare anzeigen
                    </Link>
                </div>
            </div>
        );
    }

    const rawCertificate = (seminar.certificate_content || '').trim();
    // Wurde der WYSIWYG-Editor benutzt (HTML enthalten)? Dann als HTML rendern, sonst als Bulletlist je Zeile.
    const isCertificateHtml = /<\/?(p|br|ul|ol|li|h\d|strong|em|b|i|u|a|span|div)\b/i.test(rawCertificate);
    const certificateItems = !isCertificateHtml && rawCertificate
        ? rawCertificate.split('\n').filter(line => line.trim())
        : [];

    return (
        <div className="min-h-screen bg-gray-50">
            <AdminPreviewBanner />
            {/* Header */}
            <header
                className="relative bg-secondary overflow-hidden"
                style={headerCfg.sectionStyle}
                data-testid="seminar-detail-header"
            >
                {headerCfg.hasImage && (
                    <div className="absolute inset-0 pointer-events-none" style={headerCfg.overlayStyle} aria-hidden="true" />
                )}
                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-6">
                    <div className="flex items-center justify-between">
                        <Link to="/seminare" className="inline-flex items-center gap-2 text-gray-300 hover:text-white transition-colors drop-shadow py-3 min-h-[44px]" data-testid="back-to-list">
                            <ArrowLeft className="h-4 w-4" />
                            Alle Seminare
                        </Link>
                        <div className="flex items-center gap-2">
                            {headerLogo ? (
                                <img src={headerLogo} alt="BGPR-Akademie" className="h-7 w-auto object-contain" />
                            ) : (
                                <Heart className="h-5 w-5 text-primary" />
                            )}
                            <span className="text-sm font-semibold text-white drop-shadow">BGPR-Akademie</span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
                {/* Top accent */}
                <div className={`h-2 rounded-t-2xl ${isExternal ? 'bg-blue-500' : 'bg-primary'}`} />

                <div className="bg-white rounded-b-2xl shadow-sm border border-gray-100 border-t-0" data-testid="seminar-detail-card">
                    <div className="p-6 sm:p-8">
                        {/* Meta line */}
                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400 uppercase tracking-wider font-medium mb-4">
                            <span>{isExternal ? (seminar.organizer_name || 'Externer Anbieter') : 'BGPR-Akademie'}</span>
                            {!isExternal && seminar.event_category && (
                                <>
                                    <span className="text-gray-300">·</span>
                                    <span>{EVENT_CATEGORY_LABELS[seminar.event_category] || 'Fortbildung'}</span>
                                </>
                            )}
                            {!isExternal && seminar.event_format && seminar.event_format !== 'praesenz' && (
                                <>
                                    <span className="text-gray-300">·</span>
                                    <span data-testid="seminar-detail-format">{(EVENT_FORMAT_CONFIG[seminar.event_format] || EVENT_FORMAT_CONFIG.praesenz).label}</span>
                                </>
                            )}
                            <span className="text-gray-300">·</span>
                            <span>{seminar.ue_count} Unterrichtseinheiten</span>
                            {seminar.course_number && (
                                <>
                                    <span className="text-gray-300">·</span>
                                    <span className="font-mono" data-testid="seminar-detail-course-number">{seminar.course_number}</span>
                                </>
                            )}
                            {isFull && (
                                <span className="ml-auto text-amber-500 normal-case tracking-normal font-semibold">Ausgebucht</span>
                            )}
                        </div>

                        {/* Title */}
                        <div className="mb-3">
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="seminar-detail-title">
                                {seminar.title}
                            </h1>
                            {seminar.subtitle && (
                                <p className="text-base text-gray-500 mt-1" data-testid="seminar-detail-subtitle">{seminar.subtitle}</p>
                            )}
                        </div>

                        {seminar.description && (
                            <div
                                className="legal-html text-gray-600 mb-6"
                                dangerouslySetInnerHTML={{ __html: seminar.description }}
                            />
                        )}

                        {/* Info Cards */}
                        <div className="grid sm:grid-cols-2 gap-4 mb-6">
                            {(() => {
                                if (seminar.date_mode === 'range' && seminar.date_range_start) {
                                    return (
                                        <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl sm:col-span-2" data-testid="seminar-detail-date-range">
                                            <Calendar className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Zeitraum</p>
                                                <p className="text-sm font-semibold text-gray-900 mt-0.5">
                                                    {formatDate(seminar.date_range_start)} &ndash; {formatDate(seminar.date_range_end)}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">Detaillierte Termine siehe Beschreibung.</p>
                                            </div>
                                        </div>
                                    );
                                }
                                const dates = seminar.dates?.length
                                    ? seminar.dates
                                    : (seminar.date ? [{ date: seminar.date, time_start: seminar.time_start, time_end: seminar.time_end }] : []);
                                return (
                                    <div className={`flex items-start gap-3 p-4 bg-gray-50 rounded-xl ${dates.length > 1 ? 'sm:col-span-2' : ''}`} data-testid="seminar-detail-dates">
                                        <Calendar className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{dates.length > 1 ? 'Termine' : 'Datum & Uhrzeit'}</p>
                                            {dates.map((d, i) => {
                                                const fmt = d.format || seminar.event_format || 'praesenz';
                                                const fmtLabel = fmt === 'online' ? 'Online' : fmt === 'hybrid' ? 'Hybrid' : 'Präsenz';
                                                const fmtClass = fmt === 'online' ? 'bg-blue-100 text-blue-700' : fmt === 'hybrid' ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700';
                                                return (
                                                    <p key={i} className="text-sm font-semibold text-gray-900 mt-0.5 flex items-center gap-2">
                                                        <span>{formatDate(d.date)}, {d.time_start}&ndash;{d.time_end} Uhr</span>
                                                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${fmtClass}`} data-testid={`date-format-badge-${i}`}>{fmtLabel}</span>
                                                    </p>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })()}
                            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                                <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Veranstaltungsort</p>
                                    <p className="text-sm font-semibold text-gray-900 mt-0.5">{seminar.location}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                                <Users className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Teilnehmer</p>
                                    <p className="text-sm font-semibold text-gray-900 mt-0.5">
                                        {seminar.current_participants} von {seminar.max_participants} Plätzen belegt
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Referent(en) — bevorzugt aus Referenten-Stammdaten (Multi-Select),
                             Fallback auf das alte Freitext-Feld `instructor_name`. */}
                        {(seminar.referent_display || seminar.instructor_name) && (() => {
                            const display = seminar.referent_display || seminar.instructor_name;
                            const count = seminar.referent_ids?.length
                                || (display ? display.split(',').length : 1);
                            const label = count > 1 ? 'Referenten' : 'Referent';
                            return (
                                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl mb-6">
                                    <User className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
                                        <p className="text-sm font-semibold text-gray-900 mt-0.5">{display}</p>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Certificate Content */}
                        {(certificateItems.length > 0 || (isCertificateHtml && rawCertificate)) && (
                            <div className="mb-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <FileText className="h-5 w-5 text-primary" />
                                    <h3 className="text-base font-semibold text-gray-900">Seminarinhalte</h3>
                                </div>
                                {isCertificateHtml ? (
                                    <div
                                        className="prose prose-sm max-w-none text-sm text-gray-700 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5"
                                        dangerouslySetInnerHTML={{ __html: rawCertificate }}
                                    />
                                ) : (
                                    <ul className="space-y-2 pl-1">
                                        {certificateItems.map((item, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                                                {item.trim()}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        {/* Price & CTA */}
                        {(() => {
                            const discounts = seminar.discounts || [];
                            const selDisc = selectedDiscountIdx >= 0 ? discounts[selectedDiscountIdx] : null;
                            const base = parseFloat(seminar.price || 0);
                            let finalPrice = base;
                            if (selDisc) {
                                if (selDisc.discount_type === 'percent') finalPrice = Math.max(0, base * (1 - (selDisc.discount_value || 0) / 100));
                                else finalPrice = Math.max(0, base - (selDisc.discount_value || 0));
                            }
                            const needsVerein = !!(selDisc && selDisc.requires_verein_name);

                            const submitBooking = async (prerequisites = []) => {
                                if (needsVerein && !vereinName.trim()) {
                                    toast.error('Bitte geben Sie den Namen Ihres Mitgliedsvereins an.');
                                    return;
                                }
                                setBooking(true);
                                try {
                                    await bookingApi.createParticipant(seminar.id, prerequisites, {
                                        discount_index: selectedDiscountIdx >= 0 ? selectedDiscountIdx : null,
                                        verein_name: needsVerein ? vereinName.trim() : null,
                                    });
                                    toast.success('Erfolgreich angemeldet!');
                                    navigate('/portal');
                                } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
                                } finally {
                                    setBooking(false);
                                }
                            };

                            return (
                            <div className="pt-6 border-t border-gray-100 space-y-5">
                                {/* Discount selector */}
                                {discounts.length > 0 && !isExternal && (
                                    <div data-testid="discount-selector">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Tag className="h-4 w-4 text-primary" />
                                            <h3 className="text-sm font-semibold text-gray-900">Seminargebühren</h3>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer">
                                                <input type="radio" name="discount" checked={selectedDiscountIdx === -1} onChange={() => { setSelectedDiscountIdx(-1); setVereinName(''); }} className="mt-0.5 accent-primary" data-testid="discount-none" />
                                                <div className="flex-1 flex items-center justify-between">
                                                    <span className="text-sm">Kein Rabatt</span>
                                                    <span className="text-sm font-semibold text-gray-700">{formatCurrency(base)}</span>
                                                </div>
                                            </label>
                                            {discounts.map((d, i) => {
                                                const p = d.discount_type === 'percent' ? Math.max(0, base * (1 - (d.discount_value || 0) / 100)) : Math.max(0, base - (d.discount_value || 0));
                                                return (
                                                    <label key={i} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${selectedDiscountIdx === i ? 'border-emerald-400 bg-emerald-50/50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                                        <input type="radio" name="discount" checked={selectedDiscountIdx === i} onChange={() => setSelectedDiscountIdx(i)} className="mt-0.5 accent-emerald-600" data-testid={`discount-option-${i}`} />
                                                        <div className="flex-1">
                                                            <div className="flex items-center justify-between gap-2">
                                                                <span className="text-sm font-medium text-gray-900">{d.name}</span>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs font-medium text-emerald-700">−{d.discount_value}{d.discount_type === 'percent' ? '%' : ' €'}</span>
                                                                    <span className="text-sm font-bold text-gray-900">{formatCurrency(p)}</span>
                                                                </div>
                                                            </div>
                                                            {d.description && <p className="text-xs text-gray-500 mt-0.5">{d.description}</p>}
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        {/* Verein name input when required */}
                                        {needsVerein && (
                                            <div className="mt-3 p-3 bg-emerald-50/40 border border-emerald-200/50 rounded-xl">
                                                <label className="block text-sm font-medium text-gray-900 mb-1.5">
                                                    Name Ihres Mitgliedsvereins <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={vereinName}
                                                    onChange={(e) => setVereinName(e.target.value)}
                                                    placeholder="z.B. Herzsport Berlin-Mitte e.V."
                                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                                                    data-testid="verein-name-input"
                                                    required
                                                />
                                                <p className="text-xs text-gray-500 mt-1.5">Wird Ihrem Profil als „tätig für:" hinzugefügt.</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Kostenfrei-Hinweis */}
                                {base <= 0 && (
                                    <div className="rounded-xl border border-teal-200 bg-teal-50/70 px-4 py-3 flex items-start gap-3" data-testid="free-seminar-notice">
                                        <CheckCircle className="h-5 w-5 text-teal-600 mt-0.5 shrink-0" />
                                        <div className="text-sm text-teal-800">
                                            <p className="font-semibold">Diese Veranstaltung ist kostenfrei.</p>
                                            <p className="text-teal-700/90 mt-0.5">Es wird keine Rechnung erstellt. Sie erhalten nach Teilnahme eine Teilnahmebescheinigung.</p>
                                        </div>
                                    </div>
                                )}

                                {/* Anmeldeschluss-Hinweis */}
                                {deadlineDisplay && !isExternal && (
                                    <div className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${registrationClosed ? 'border-gray-200 bg-gray-50' : 'border-amber-200 bg-amber-50/60'}`} data-testid="detail-deadline-notice">
                                        <Clock className={`h-5 w-5 mt-0.5 shrink-0 ${registrationClosed ? 'text-gray-400' : 'text-amber-600'}`} />
                                        <div className="text-sm">
                                            {registrationClosed ? (
                                                <p className="text-gray-700"><span className="font-semibold">Anmeldeschluss erreicht.</span> Anmeldungen waren bis {deadlineDisplay} möglich.</p>
                                            ) : (
                                                <p className="text-amber-900">Anmeldung möglich <span className="font-semibold">bis {deadlineDisplay}</span>.</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Hinweis für Gäste: Buchung erfolgt für die Person des Accounts.
                                    Verhindert das Problem, dass Einrichtungsleiter Mitarbeiter
                                    unter eigener Mail registrieren (Production-Feedback 05/2026). */}
                                {!isAuthenticated && !isExternal && !registrationClosed && (
                                    <div
                                        className="rounded-xl border border-blue-200 bg-blue-50/70 px-4 py-3 flex items-start gap-3"
                                        data-testid="guest-booking-notice"
                                    >
                                        <Info className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                                        <div className="text-sm text-blue-900 space-y-1">
                                            <p>
                                                <span className="font-semibold">Sie buchen dieses Seminar für sich selbst.</span>
                                                {' '}Die Teilnahmebescheinigung wird Ihrem persönlichen Konto zugeordnet.
                                            </p>
                                            <p className="text-blue-800/90">
                                                Möchten Sie Kolleginnen oder Mitarbeiter anmelden? Bitte lassen Sie diese
                                                jeweils ein <strong>eigenes Konto</strong> anlegen — so erhalten alle ihre
                                                Bescheinigungen unter ihrer eigenen E-Mail-Adresse.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Gesamtbetrag + CTA */}
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                    <div>
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Gesamtbetrag</p>
                                        <div className="flex items-baseline gap-2">
                                            <p className="text-3xl font-bold text-gray-900" data-testid="final-price">{formatCurrency(finalPrice)}</p>
                                            {selDisc && finalPrice < base && (
                                                <p className="text-sm text-gray-400 line-through">{formatCurrency(base)}</p>
                                            )}
                                        </div>
                                    </div>

                                    {hasExternalRegistration ? (
                                        <a
                                            href={seminar.external_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`inline-flex items-center gap-2 px-6 py-3 text-white font-semibold rounded-xl transition-colors ${isExternalOrganizer ? 'bg-blue-600 hover:bg-blue-700' : 'bg-primary hover:bg-primary/80'}`}
                                            data-testid="detail-external-link"
                                        >
                                            Zur Anmeldung beim Veranstalter
                                            <ExternalLink className="h-4 w-4" />
                                        </a>
                                    ) : registrationClosed ? (
                                        <span
                                            className="inline-flex items-center gap-2 px-6 py-3 font-semibold rounded-xl bg-gray-100 text-gray-500 border border-gray-200"
                                            title={deadlineDisplay ? `Anmeldeschluss war am ${deadlineDisplay}` : 'Anmeldeschluss überschritten'}
                                            data-testid="detail-deadline-closed"
                                        >
                                            Anmeldeschluss erreicht
                                        </span>
                                    ) : (
                                        <button
                                            onClick={() => {
                                                if (!isAuthenticated) {
                                                    navigate(`/teilnehmer/registrieren?seminar=${seminar.id}`);
                                                    return;
                                                }
                                                if (seminar.requires_prerequisites) { setPrereqOpen(true); return; }
                                                submitBooking([]);
                                            }}
                                            className={`inline-flex items-center gap-2 px-6 py-3 font-semibold rounded-xl transition-colors ${
                                                isFull || booking ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/80'
                                            }`}
                                            disabled={isFull || booking}
                                            data-testid="detail-register-button"
                                        >
                                            {isFull ? 'Leider ausgebucht' : (booking ? 'Wird gebucht...' : 'Jetzt anmelden')}
                                            {!isFull && !booking && <ArrowRight className="h-4 w-4" />}
                                        </button>
                                    )}
                                </div>
                            </div>
                            );
                        })()}
                    </div>
                </div>
            </main>

            {/* Prerequisites Dialog */}
            {seminar && (
                <PrerequisitesDialog
                    open={prereqOpen}
                    onOpenChange={setPrereqOpen}
                    seminarTitle={seminar.title}
                    onConfirm={async (selected) => {
                        setPrereqOpen(false);
                        const discounts = seminar.discounts || [];
                        const selDisc = selectedDiscountIdx >= 0 ? discounts[selectedDiscountIdx] : null;
                        if (selDisc?.requires_verein_name && !vereinName.trim()) {
                            toast.error('Bitte geben Sie den Namen Ihres Mitgliedsvereins an.');
                            return;
                        }
                        setBooking(true);
                        try {
                            await bookingApi.createParticipant(seminar.id, selected, {
                                discount_index: selectedDiscountIdx >= 0 ? selectedDiscountIdx : null,
                                verein_name: selDisc?.requires_verein_name ? vereinName.trim() : null,
                            });
                            toast.success('Erfolgreich angemeldet!');
                            navigate('/portal');
                        } catch (error) {
                            toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
                        } finally {
                            setBooking(false);
                        }
                    }}
                />
            )}

            {/* Footer */}
            <footer className="bg-secondary mt-16 py-8">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
                    <div className="flex items-center justify-center gap-2 mb-3">
                        {headerLogo ? (
                            <img src={headerLogo} alt="BGPR-Akademie" className="h-5 w-auto object-contain" />
                        ) : (
                            <Heart className="h-4 w-4 text-primary" />
                        )}
                        <span className="text-sm font-semibold text-white">BGPR-Akademie</span>
                    </div>
                    <p className="text-xs text-gray-300 mb-3">
                        Berliner Gesellschaft für Prävention und Rehabilitation von Herz-Kreislauferkrankungen e.V.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-gray-300">
                        <button type="button" onClick={() => setLegalOpen('imprint')} className="hover:text-white transition-colors py-2 px-1 min-h-[44px] inline-flex items-center" data-testid="footer-link-imprint">Impressum</button>
                        <span className="text-gray-500 hidden sm:inline">|</span>
                        <button type="button" onClick={() => setLegalOpen('privacy_policy')} className="hover:text-white transition-colors py-2 px-1 min-h-[44px] inline-flex items-center" data-testid="footer-link-privacy">Datenschutzhinweis</button>
                        <span className="text-gray-500 hidden sm:inline">|</span>
                        <button type="button" onClick={() => setLegalOpen('terms_of_service')} className="hover:text-white transition-colors py-2 px-1 min-h-[44px] inline-flex items-center" data-testid="footer-link-terms">Nutzungsbedingungen</button>
                    </div>
                </div>
            </footer>
            <LegalDialog type={legalOpen} open={!!legalOpen} onOpenChange={(v) => !v && setLegalOpen(null)} />
        </div>
    );
}
